// file to add global interfaces

import { HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { fill, includes } from 'lodash';
import { ProgramConfiguration } from '@services/url-address-config/url-address-config.types';
import { percentString } from 'chartjs-plugin-annotation';
import { IPromotionCodeStatus } from '@services/bonus/bonus.service.types';
import { ISelectOption } from '@app/shared/types/inputs';
import { ColumnManagementOptionsType } from '@pages/statistics/shared/shared-statistics-functions/shared-statistics-functions.component';

export class CoreModels {}

/**
 * @NgModule CoreModule
 * @description
 *
 * User and user menu basic info interface
 */
export interface IUserBasicInfo {
  user?: IUserData;
  menu?: IMenuData;
  permissions?: string[];
  personal_settings?: IPersonalSettings;
  send_activity_data?: boolean;
  latest_news_id?: number;
}

export interface IPersonalSettings {
  dashboard?: IDashboardWidgetsData;
  lockers?: any; // getting nulls atm, we will type it when we know what is needed in here
  other?: any; // getting nulls atm, we will type it when we know what is needed in here
  programs?: any; // getting nulls atm, we will type it when we know what is needed in here
  statistics?: any; // getting nulls atm, we will type it when we know what is needed in here
  webmaster?: any; // getting nulls atm, we will type it when we know what is needed in here
}

export type DashboardSecondRowTypes = 'small-small' | 'medium';
export type DashboardThirdRowTypes = 'medium-large' | 'medium-medium-medium';
export type DashboardBoxPosition = 'left' | 'right' | 'top' | 'bottom' | 'middle';
export type DashboardRows = 'second_row' | 'third_row';

export enum SecondRowType {
  SmallSmall = 'small-small',
  Medium = 'medium'
}

export interface IDashboardWidgetsData {
  first_row?: {
    boxes: {
      widget: string;
      settings?: IWidgetSettings;
    }[];
  } | null;
  main_stats?: {
    boxes: {
      widget: string;
      settings?: IWidgetSettings;
    }[];
  } | null;
  second_row?: {
    type?: DashboardSecondRowTypes;
    boxes?: IDashboardWidgetBoxData[];
  };
  third_row?: {
    type?: DashboardThirdRowTypes;
    boxes?: IDashboardWidgetBoxData[];
  };
}

export interface IDashboardWidgetBoxData {
  position: DashboardBoxPosition;
  widget: string;
  settings?: IWidgetSettings;
}

export interface IWidgetSettings {
  selected_tab_menu?: string;
  selected_items?: string[] | ISelectOption | ISelectOption[] | ColumnManagementOptionsType;
  selected_period?: {
    date_from: string;
    date_to: string;
  };
  selected_campaign_id?: number;
  sort_method?: {
    name?: string;
    additional?: string;
    orderField?: string;
    orderType?: string;
    id?: number;
  };
  selected_chart_data?: string[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * User basic info interface
 */
export interface IUserData {
  user_id?: number;
  username?: string;
  email?: string;
  withdrawal_available?: number;
  current_language?: string;
  currency?: string;
  avatar?: string;
  country_code?: string;
  lead_notification_sound?: string;
  country_by_ip?: string;
  rules_accept_date?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * User descoration interface
 */

export interface IUserDecoration {
  id: number;
  name: string;
  img: string;
  description?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * menu for current user interface
 */
export interface IMenuData {
  menu_left?: IMenuItemData[];
  menu_right?: string[];
  admin_menu_left?: IMenuItemData[];
}

export interface IMenuItemData {
  name?: string;
  new?: boolean;
  children?: IMenuItemData[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Marketing data interface
 */
export interface IMarketingData {
  name?: string;
  url?: string;
  img?: string;
  icon?: string;
  nofollow?: boolean;
}
export interface IDashboardBannerData {
  id: number;
  description: string;
  image: string;
  position: string;
  title: string;
  url: string;
  color?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Choosen sort value interface
 */
export interface IChosenSortValue {
  icon?: string;
  name?: string;
  descending?: boolean;
  value?: string;
  id?: number;
  imgSrc?: string;
  additional?: string;
  orderType?: string;
  orderField?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Choosen sort value interface
 */
export interface ISortValues {
  sort_values?: IChosenSortValue[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * OptionalAction interface
 */
export interface IOptionalActionInfo {
  page_path?: string;
  optional_action_required?: boolean;
  action_name?: string;
}

export interface IOptionalActions {
  optional_actions?: IOptionalActionInfo[];
}

export interface ICountry {
  active?: boolean;
  code: string;
  id: number;
}

export interface ICategoryType {
  id?: number;
  children?: ICategoryType[];
}

export interface IDeviceType {
  id?: number;
  name?: string;
  children?: IDeviceType[];
}

interface IIncentTypesType {
  children: any[];
  id: number;
}

export interface ICampaignType {
  id?: number;
  system_name?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Single campaign interface
 */
export interface ISingleCampaign {
  active?: boolean;
  access_information?: string;
  access?: boolean;
  blocked_reason?: string;
  additional_requirements?: boolean;
  redirect_url?: string;
  payment?: string;
  income?: number;
  currency?: string;
  beginner?: boolean;
  is_new?: boolean;
  views?: number;
  earn?: number;
  id?: number;
  name?: string;
  logo?: string;
  preview_url?: string;
  type?: ICampaignType[] | string;
  recommended?: boolean;
  short_description?: Array<object>;
  categories?: IDeviceType[];
  // countries?: Array<object>;
  countries?: ICountry[];
  activated_at?: string;
  likes?: number;
  like_status?: boolean;
  lang_name?: Array<any>;
  description?: Array<object>;
  status?: string;
  reject_reason?: string;
  live?: boolean;
  restriction?: Array<Record<string, string>>;
  devices?: Array<{ device_type_id: number }>;
  device_types?: IDeviceType[];
  incent_types?: IIncentTypesType;
  conversion_types?: Array<number>;
  banners?: boolean;
  rate?: number;
  cr?: any;
  epc?: string;
  users_rating_average?: number;
  users_rating_number?: number;
  statistics?: object;
  updated_at?: string;
  deeplink?: boolean;
  payout_time?: number;
  cookies?: number;
  configuration_count?: number;
  conversion_rate?: string;
  capture_url?: string;
  user_traffic_source?: boolean;
  exclusive?: boolean;
  exclusive_user_has_access?: boolean;
  goals?: IGoal[];
  has_banners?: boolean;
  has_deep_link?: boolean;
  has_products?: boolean;
  has_hidelink?: boolean;
  has_coupons?: boolean;
  program_id?: number;
  is_smartlink?: boolean;
  admin_smartlink_rates_by_countries?: Record<string, string>;

  // TODO: remove below
  campaign_restrictions?: string[];
  last_update?: string;
  traffic_type?: string[];
  is_live?: boolean;
  dislikes?: number;
  active_configurations?: number;
  additional_functionalities?: string;
  campaign_attributes?: Array<object>;
  conversion?: string;
  joined?: boolean;
  payout?: string;
  conversion_type?: string;
  campaign_image?: string;
  news?: string;
  banner?: string;
  campaign_description_short?: string;
  campaign_description_long?: string;
  device_type?: string;
  start_date?: string;
  user_rating_access?: boolean;
  rating?: number;
  user_rating_average?: number | null;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Pagination interface
 */
export interface IPagination {
  total?: number;
  count?: number;
  per_page?: number;
  current_page?: number;
  total_pages?: number;
  links?: {
    next_page?: string | null;
    previous_page?: string | null;
  };
}

/**
 * @NgModule CoreModule
 * @description
 *
 */
export interface IPromotionCodesHistoryData {
  pagination?: IPagination;
  promotion_codes?: IBonus[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Checked campaign filter value interface
 */
export interface ICheckedValue {
  id?: string | number;
  index?: number;
  quantity?: number;
  childs?: IFilterSubitem[];
}

export interface PayoutAmount {
  id: number;
  text: string;
  icon: string;
  data: number;
  currency: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Campaign filter subitem interface
 */
export interface IFilterSubitem {
  id?: string;
  quantity?: number;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Campaign filter item interface
 */
export interface IFilterItem {
  id?: string;
  code?: string;
  quantity?: number;
  filter_name?: string;
  childs?: Array<IFilterItem>;
  translated?: string;
}

export type IFilterGroupFilters = Record<string, string | number | (string | number)[] | IFilterItem[]>;

/**
 * @NgModule CoreModule
 * @description
 *
 * Campaign filter group interface
 */
export interface IFilterGroup {
  filter_type?: string;
  filter_name?: string;
  filters?: IFilterGroupFilters | IFilterGroupFilters[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Campaign filters interface
 */
export interface IFilters {
  campaign_filters?: IFilterGroup[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Sidebar subitem interafce
 */
export interface ISidebarSubitem {
  menu_subitem: string;
  router_link: string;
  icon: string;
  active: string;
  new: boolean;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Sidebar item interafce
 */
export interface ISidebarItem {
  menu_item: string;
  router_link: string;
  icon: string;
  active: boolean;
  sub_elements?: ISidebarSubitem[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Sidebar menu group interafce
 */
export interface ISidebarGroup {
  group_name: string;
  group_elements: ISidebarItem[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Sidebar interface
 */
export interface ISidebar {
  sidebar_menu: ISidebarGroup[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Local/global alert interface
 */
export interface IAlert {
  headerText: string;
  infoText: string;
  infoTextAdditionalLink?: any;
  buttonText: string;
  image: string;
  imageClass?: string;
  link?: string;
  modal?: string;
  highlightedText?: any;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * translated country interface
 */
export interface ITranslatedCountry {
  originalName: string;
  translatedName: string;
}

export interface ISingleBanner {
  id: number;
  img: string;
  width: number;
  height: number;
}

export interface ISelectedConfiguration {
  id?: number;
  configuration_id?: number;
  img?: string;
  width?: number;
  height?: number;
  name?: string;
  description?: string;
  prices?: Array<object>;
  product_img?: string;
}

export interface IURLConfiguration {
  id: number;
  name: string;
  trackingDomain: string;
  url: string;
  creationDate: string;
  visits: number;
  profits: number;
  currency: string;
}

export interface IBannerConfiguration {
  id: number;
  urlConfiguration?: IURLConfiguration;
  trackingDomain?: string;
  width: number;
  height: number;
  img: string;
  generatedCode?: string;
  creationDate?: string;
}

export interface IBannerConfigurationData {
  banner_id?: number;
  configuration_id?: number;
  program_id?: number;
  program_name?: string;
  created_at?: string;
  height?: number;
  width?: number;
  img?: string;
  program_config_name?: string;
  script?: string;
  updated_at?: string;
}

export interface IBannerConfigurationsData {
  banners?: IBannerConfigurationData[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Notification item interafce
 */
export interface INotificationItem {
  id: number;
  created_at: string;
  shown: number;
  text: string;
  type?: number;
  link?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Notification category group interafce
 */
export interface INotificationGroup {
  category: string;
  results: INotificationItem[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Notifications interface
 */
export interface INotifications {
  notifications: Array<INotificationGroup>;
}

type ISupportPanelContact = {
  contact: string;
  link: string;
  subjects: string[];
  username: string;
};

/**
 * @NgModule CoreModule
 * @description
 *
 * Support panel interafce
 */
export interface ISupportPanel {
  name?: string;
  slug?: string;
  type?: string;
  contents: ISupportPanelContents[];
  descriptions?: string[];
  contacts?: ISupportPanelContact[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Support panel category content interafce
 */
export interface ISupportPanelContents {
  title?: string;
  content?: string;
  group?: string;
  // this parameter is set within faq-section component
  visible?: boolean;
}

export interface IActiveSection {
  section: string;
  id: number;
}

export interface IProduct {
  id?: number;
  name?: string;
  product_img?: string;
  prices?: any[];
  date_added?: string;
  description?: string;
  // TODO reomve below
  img?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Array of single campaigns interface
 */

export interface IStatisticsSummary {
  id: number;
  campaign_name?: number;
  campaign_image?: string;
  campaign_types: any;
  currency: string;
  leads: number;
  leads_details: [];
  earns: number;
  earns_details: any;
  visits: number;
  conversion: number;
  acceptance: number;
  epc: number;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Conversation panel view state interface
 */
export interface IConversationsState {
  minimized: boolean;
  maximized: boolean;
}

export interface IStatisticsTilesResultsItem {
  id: number;
  status: string;
  value: number | string;
  icon: string;
  ext: string;
  percent?: boolean;
}

export interface IStatisticsTiles {
  subcategory?: string;
  value?: any;
  icon?: string;
  results?: IStatisticsTilesResultsItem[];
  currency?: string;
  middle?: string;
}

export interface IStatisticsTilesAppliedFilters {
  leads: string[];
  earnings: string[];
  visits: string[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Array of statistics
 */
export interface IStatistics {
  stats?: IStatisticsTiles[];
  statistics?: IStatisticsSummary[];
  pagination?: any;
  tiles?: any;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Current User in chat messages
 */
export interface IChatUser {
  avatar?: string;
  country_code?: string;
  country_id?: number;
  decorations?: IUserDecoration[];
  is_administrator?: boolean;
  is_conversation_allowed?: boolean; // user is not allowed to communicate with support
  online?: boolean;
  user_id?: number;
  username?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * General chat message interface
 */
export interface IChatMessage {
  id?: number;
  user_id?: number;
  rank?: string; // support-blue | admin | support
  username?: string;
  decorations?: IUserDecoration[];
  avatar?: string;
  hearts?: number;
  created_at?: string;
  message?: string;
}

export interface ISocketChatMessage {
  avatar?: string;
  created_at?: string;
  id?: number;
  message?: string;
  type?: number;
  type_message?: string;
  username?: string;
  username_chat_color?: string;
}

export interface ISocketPrivateMessage {
  avatar?: string;
  id?: number;
  text?: string;
  type?: string;
  user_id?: number;
  username?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * General chat current user info and messages interface
 */
export interface IGeneralChat {
  active_status?: 'active' | 'inactive' | 'banned';
  banned_reason?: string | null;
  chat?: IChatMessage[];
}

export interface ISocialMediaItem {
  name?: string;
  url?: string;
  icon_url?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Statistics basic info inteface
 */
export interface IStatsBasic {
  campaign_name?: string;
  config_name?: string;
  rate?: string;
  status?: object;
  countries?: Array<string>;
  referring_address?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Leads statistics extendable info inteface
 */
export interface ILeadStatsExtendable {
  ml_sub?: Array<string>;
  transaction_id?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Single program statistics interface
 */
export interface ITableSingleProgramStats {
  id?: number;
  basic_info?: IStatsBasic;
  extendable?: ILeadStatsExtendable;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Statistics basic data interface
 */
export interface ITableDataStats {
  table?: IStatisticsTableStructure;
  results?: ITableSingleProgramStats[];
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Statistics table structure interface
 */
export interface IStatisticsTableStructure {
  extendable?: boolean;
  headers?: Array<string>;
}

export type RegistrationSteps = 'step1' | 'step2' | 'via-socials' | 'step3' | 'registration-complete';

export interface ChangeRegistrationStepData {
  step: RegistrationSteps;
  extraData?: {
    username?: string;
    email?: string;
    currency?: string;
    source_information?: any;
    earn_other_network_information?: any;
    social_medias?: any;
  };
}

export interface GoogleVerifyResponse {
  message?: string;
  required_fields?: string[];
  cache_id: string;
  user_access_tokens: Tokens;
}

export interface Tokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  change_password_required?: boolean;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * User login data model
 */
export interface UserLogin {
  grant_type: string;
  client_id: string;
  client_secret: string;
  username?: string;
  password?: string;
  token?: string;
  activity_params?: string;
  _ga?: string | null;
  ga_link_id?: number;
  gclid?: string | null;
  recaptcha?: string;
}

/**
 * @NgModule CoreModule
 * @description
 *
 * Selected language interface
 */
export interface ISelectedLanguage {
  name: string;
  script: string;
  native: string;
  language_code: string;
  native_short: string;
  country_prefix: string;
}

/**
 * Statistics table structure interface
 */
export interface IVRLayouts {
  id: number;
  name: string;
  icon: string;
}

export interface IUserPreferences {
  timezone?: string;
  show_info?: boolean;
  show_decorations?: boolean;
  show_stats?: boolean;
  hide_chat?: boolean;
}

export interface IGeneralUserSettings {
  language?: string;
  currency?: string;
  preferences?: IUserPreferences;
  sounds?: ISound[];
}

export interface IUserPersonalData {
  name?: string;
  surname?: string;
  avatar?: string;
  birth_date?: string;
  description?: string;
  tshirt_selected?: string;
  tshirt_size?: string;
  contact_data?: IContactData;
  address?: IAddress;
  account_details?: IAccountDetails;
}

export interface IUserPersonalDataFlat {
  name?: string;
  surname?: string;
  avatar?: string;
  birth_date?: string;
  description?: string;
  tshirt_selected?: string;
  tshirt_size?: string;
  email?: string;
  phone?: string;
  country?: string;
  street?: string;
  post_code?: string;
  city?: string;
  state?: string;
  account_type?: number;
  company_name?: string;
  nip_number?: string;
  phone_verified?: boolean;
}

export interface IAccountDetails {
  account_type?: number;
  company_details?: {
    company_name?: string;
    nip_number?: string;
  };
}

export interface IUserSecurity extends Record<string, any> {
  user_2fa_active?: 'active' | 'inactive';
  blocked_mobile_auth?: boolean;
  security?: ISecurity;
}

export interface ISecurity {
  old_password?: string;
  password?: string;
  password_confirmation?: string;
  logout_devices?: boolean;
}

export interface I2FADetails {
  backup_code: string;
  secret: string;
  qr_code: string;
}

export interface IPaymentsSettings {
  p_pp?: IPaymentMethodSettings;
  p_skrill?: IPaymentMethodSettings;
  p_tether?: IPaymentMethodSettings;
  p_usdc?: IPaymentMethodSettings;
  p_revolut?: IPaymentMethodSettings;
  p_bitcoin?: IPaymentMethodSettings;
  p_capitalist?: IPaymentMethodSettings;
  p_bank?: IPaymentMethodSettings;
}

export interface IEmailNotificationsSettings {
  exclusive_marketing?: INotificationSetting;
  earnings_report?: INotificationSetting;
  daily_report?: INotificationSetting;
  new_partner_programs?: INotificationSetting;
  payment_status?: INotificationSetting;
  campaign_join?: INotificationSetting;
  no_activity?: INotificationSetting;
  no_earnings?: INotificationSetting;
}

export interface ISmsNotificationsSetting {
  sms_payment?: INotificationSetting;
  sms_promotion_code?: INotificationSetting;
  sms_notification?: INotificationSetting;
}

export interface IPopupNotificationsSetting {
  notification_ppi?: INotificationSetting;
}

/**
 * Settings and preferences interfaces
 */
export interface IUserSettings {
  allowed_to_change_currency?: boolean;
  general?: IGeneralUserSettings;
  personal_data?: IUserPersonalData;
  security?: IUserSecurity;
  social_media?: ISocialMediaAccount[];
  payments?: IPaymentsSettings;
  notifications?: IUserNotificationSettings;
}

export interface ICollection {
  collection: 'general' | 'personal_data' | 'security' | 'social_media' | 'payments' | 'notifications';
  values: any;
  subcollection: string | number;
}

export interface ISound {
  id?: number;
  name?: string;
  selected?: boolean;
  src?: string;
}

export interface IContactData {
  email?: string;
  phone?: string;
  phone_verified?: boolean;
}

export interface IAddress {
  country?: string;
  state?: string;
  street?: string;
  post_code?: string;
  city?: string;
}

export interface ISocialMediaAccount {
  id?: number;
  name?: string;
  value?: string;
  is_main?: boolean;
}

export interface IPaymentMethodSettings {
  system_name?: string;
  active?: boolean;
  values?: Record<string, string>;
  name?: string;
}

export interface IUserNotificationSettings {
  email_notifications?: IEmailNotificationsSettings;
  sms_notifications?: ISmsNotificationsSetting;
  popup_notifications?: IPopupNotificationsSetting;
}

export interface IPaymentMethod {
  system_name: string;
  name?: string;
  icon: string;
  active: boolean;
  provision: number;
  provisionMessage: string;
  value: number;
  currency: string;
  message?: string;
  messageValue?: string;
  values?: {
    wallet_usdt?: string;
    wallet_usdt_network?: string;
    wallet_usdc?: string;
    wallet_usdc_network?: string;
  };
}

export interface INotificationSetting {
  system_name?: string;
  active?: boolean;
  value?: null | string;
  values?: Record<string, number> | string[];
}

/*
 * Search panel data interfaces
 */
export interface IProgramSearchResult {
  id: number;
  name: string;
  logo: string;
  provider_id: number;
  ivr: boolean;
  sms_chat: boolean;
}

export interface ITopicSearchResult {
  id: number;
  name: string;
  logo: string;
  category_id: number;
  category_url: string;
}

export interface IUserSearchResult {
  id: number;
  name: string;
  logo: string;
  link?: string;
  program_id?: number;
  category_id?: number;
  category_url?: string;
}

export interface IConfigurationSearchResult {
  program_id: number;
  name: string;
  logo: string;
  provider_id: number;
}

export interface ITabsResult {
  translation: string;
  name: string;
}

export interface ISearchResults {
  programs: IProgramSearchResult[];
  topics: ITopicSearchResult[];
  users: IUserSearchResult[];
  configs: IConfigurationSearchResult[];
  tabs: ITabsResult[];
}

export interface ICategoryItem {
  name: string;
  translation: string;
  quantity: number;
}

export interface IPaymentUserBalance {
  accepted: number;
  payment_waiting: number;
  waiting: number;
  rejected: number;
  currency: string;
}

export interface IGalleryItem {
  id: number;
  user_id: number;
  username: string;
  avatar: string;
  file: string;
  likes: number;
  dislikes: number;
  like_status: null | number;
  created_at: string;
  updated_at: string;
}

export interface ICouponsActiveFilter {
  id?: number;
  filter_name?: string;
  name?: string;
  childs?: any;
  active?: boolean;
  checked?: boolean;
  quantity?: number;
  isOpen?: boolean;
}

export interface ICouponsFilters {
  id?: number;
  filter_type: string;
  filter_name: string;
  isOpen?: boolean;
  filters: ICouponsActiveFilter[];
}

export interface ICouponsData {
  id: number;
  program_id: number;
  program_name: string;
  status: string;
  name: string;
  img: string;
  description: any;
  short_description: any;
  start_date: string;
  end_date: string;
  system_type: number;
  value: string;
  currency: string;
}

export interface ISingleCouponAction {
  program_id: number;
  program_name: string;
  button: string;
}

export interface ISingleOffer {
  id: number;
  name: string;
  leads: number;
  earns: number;
  preview_url: string;
  preview_script_url: string;
  created_at: string;
  currency: string;
}

export interface IhideLinkCampaign {
  id: number;
  name: string;
  checked: boolean;
}

export interface IfaqSection {
  id: number;
  name: string;
  group: string;
  data: ISupportPanelContents[];
}

export interface ISingleHideLink {
  id: number;
  currency?: string;
  name: string;
  program_id: number;
  program_name: string;
  hidelink_uuid: string;
  safe_page: string;
  destination: string;
  created_at: Date;
  updated_at: Date;
  earns_sum: number;
  visits_sum: number;
  deeplink_source_url?: string;
}

export interface IBonus {
  activate_date?: string;
  activation_date?: string;
  expiration_date?: string;
  name?: string;
  status?: string;
  type?: number;
  value?: number;
}

export interface IDatePickerRanges {
  range?: string;
  dateFrom?: NgbDate;
  dateTo?: NgbDate;
  translation?: any;
  active?: any;
}

export interface INavOption {
  id: number; // 1,2 3
  title: string;
  icon: string;
  active: boolean;
  hasError: boolean;
}

export interface IDropdownOption {
  id: number;
  name: string;
  value: string | number | null;
}

export interface IRadioOption {
  id: number;
  value: string | number;
  translation: string;
}

export interface IPromotionCode {
  color?: string;
  description?: string;
  image?: string;
  status?: IPromotionCodeStatus;
  title?: string | null;
  value?: string;
  promotion_bonus_id?: number | null;
  dedicated_id?: number | null;
}
export interface IPromotionCodesData {
  promotion_codes?: Record<string, IPromotionCode>;
}

export interface ILockerFields {
  type?: 'cpa_locker' | 'file_locker' | 'captcha_locker' | 'mobile_rewards';
  name?: string;
  locker_id?: string;
  needed_conversions?: number; // 1+
  load_type?: loadTypes;
  blocked_ip?: string | null;
  load_after?: number; // 1+
  close_type?: 'close' | 'redirect';
  close_redirect?: string | null;
  unlock_time?: number;
  unlock_format?: 'm' | 'h' | 'd';
  epc_value?: number;
  is_adult?: number | boolean | string;
  desktop_title?: string;
  desktop_title_description?: string;
  desktop_description?: string;
  desktop_background_transparency?: number; // 0-100
  desktop_template_type?: number; //  1 - 6
  desktop_customize_template?: number; // 0 = no, 1 = yes, 2 = expert
  desktop_max_offer_number?: number;
  desktop_close_message?: string | null;
  desktop_custom_css?: null | string;
  mobile_title?: string;
  mobile_title_description?: string;
  mobile_description?: string;
  mobile_template_type?: number; //  1 - 6
  mobile_customize_template?: number; // 0 = no, 1 = yes, 2 = expert
  mobile_custom_css?: null | string;
  mobile_max_offer_number?: number;
  mobile_offer_priority?: number; // we don't use it atm
  desktop_color1?: string;
  desktop_color2?: string;
  desktop_color3?: string;
  desktop_color4?: string;
  desktop_color5?: string;
  desktop_color6?: string;
  desktop_color7?: string;
  desktop_color8?: string;
  desktop_color9?: string;
  mobile_color1?: string;
  mobile_color2?: string;
  mobile_color3?: string;
  mobile_color4?: string;
  mobile_color5?: string;
  mobile_color6?: string;
  mobile_color7?: string;
  mobile_color8?: string;
  mobile_color9?: string;
  desktop_font?: string;
  mobile_font?: string;
  desktop_offer_button_radius?: string;
  mobile_offer_button_radius?: string;
  templates?: ITemplateStyles[];
  preview_url?: string;
  program_id?: number;

  template_type?: number; // captcha locker
  offer_priority?: number; // we don't use it atm
  min_offer_number?: number;
  max_offer_number?: number;
  mobile_desc?: string;
  desktop_desc?: string;

  title?: string; // file locker
  note?: string;
  unlock_time_format?: 'm' | 'h' | 'd';
  close_message?: string;
  customize_template?: number;
  custom_css?: string;
  custom_html_base?: string;
  custom_html_offer?: string;
  custom_html_offer_mobile?: string;
  file_name?: string;
  file_size?: string;
  button_text?: string;
  header_text?: string;
  step1?: string;
  step2?: string;
  step3?: string;
  offer_subtitle?: string;
  subtitle?: string;
  color1?: string;
  color2?: string;
  color3?: string;
  color4?: string;
  color5?: string;
  color6?: string;
  color7?: string;
  color8?: string;

  header_type?: 'text' | 'image'; // mobile rewards
  header_image?: File | string | null;
  description?: string;
  exclude_cpa?: number | boolean;
  instructions?: string;
  login_background_image?: File | string | null;
  login_description?: string;
  login_title?: string;
  prizes?: ILockerPrize[];
}

export interface ILockerPrize {
  prize_id?: string;
  name: string;
  points: number;
  image?: File | string | null;
  action_redirect?: string;
  action_email?: string;
  additional_question?: string;
  created_at?: Date;
  description: string;
  action_type: 'redirect' | 'email';
  use_question: number; // 0 = no, 1 = yes
}

export interface IVideoLockerFields {
  adult?: 0 | 1 | null;
  button_content?: { verify: string; activate: string }[];
  color_1?: string | null;
  color_2?: string | null;
  color_3?: string | null;
  color_4?: string | null;
  color_5?: string | null;
  content?: {
    video_url: string | null;
    video_img: string | null;
    video_time: string | null;
    height: string | null;
    width: string | null;
  };
  invitation_content?: { none: string; send_sms: string }[];
  panel?: 0 | 1 | null;
  url?: string | null;
  name?: string;
  website?: string;
  length?: number;
  invitation?: string;
  invitation_name?: string;
  invitation_name_default?: string;
  button?: string;
  button_name?: string;
  button_name_default?: string;
  locker_id?: string;
}

export interface IPremiumLockerFields {
  button_content: { verify: string; activate: string }[];
  color_1: string | null;
  color_2: string | null;
  color_3: string | null;
  color_4: string | null;
  color_5: string | null;
  content: string;
  invitation_content: { none: string; send_sms: string }[];
  url?: string | null;
  name?: string;
  website: string;
  transparent: 0 | 1;
  modal: 0 | 1;
  mode: string;
  modes?: string[];
  invitation?: string;
  invitation_name?: string;
  invitation_name_default?: string;
  button?: string;
  button_name?: string;
  button_name_default?: string;
  locker_id?: string;
}

export enum loadTypes {
  on_ready = 'on_ready',
  timer = 'timer',
  on_click = 'on_click'
}

export interface ITemplateStyles {
  name?: string;
  id?: number;
  style_url?: string;
  desktop_color1?: string;
  desktop_color2?: string;
  desktop_color3?: string;
  desktop_color4?: string;
  desktop_color5?: string;
  desktop_color6?: string;
  desktop_color7?: string;
  desktop_color8?: string;
  desktop_color9?: string;
  mobile_color1?: string;
  mobile_color2?: string;
  mobile_color3?: string;
  mobile_color4?: string;
  mobile_color5?: string;
  mobile_color6?: string;
  mobile_color7?: string;
  mobile_color8?: string;
  mobile_color9?: string;
  color1?: string;
  color2?: string;
  color3?: string;
  color4?: string;
  color5?: string;
  color6?: string;
  color7?: string;
  color8?: string;
  desktop_font?: string;
  mobile_font?: string;
  desktop_offer_button_radius?: string;
  mobile_offer_button_radius?: string;
  file_name?: string; // file locker properties
  file_size?: string;
  button_text?: string;
  header_text?: string;
  step1?: string;
  step2?: string;
  step3?: string;
  offer_subtitle?: string;
  subtitle?: string;
}

export interface IActivity {
  id: number;
  activity: string;
  ip_address: string;
  created_at: Date;
}

export interface IWidgetColumn {
  name: string;
  translation: string;
  visible: boolean;
}

export interface IVideoLockerSmsFields {
  restriction_accept: boolean;
  experience: string;
  program_types: string;
  partner_images: any[];
  reference: string;
  methods: any[];
}

export interface IPremiumRequestData {
  premium_request_data?: IPremiumApplication;
}

export interface IPremiumApplication {
  experience: string;
  id: number;
  methods: { images: string[]; method: string; presentation: string; urls: string }[];
  partner_images: string[];
  program_types: string[];
  reference: string;
  reject_info: null | string | number | boolean;
}

export interface JSExample {
  html?: string;
  js?: string;
}

export type BaseApiResponse<D = any> = {
  data?: D;
  errors?: Record<any, any>;
  status?: string;
  status_code?: number;
};

export interface PageableApiResponse<T> extends BaseApiResponse<T> {
  data?: T & { pagination?: IPagination };
}

export interface DropdownValue {
  name: string;
  orderField: string;
  orderType: string;
  id: number;
  icon?: string;
  additional?: string;
}

type Api<T> = Record<string, T[]>;

type PaginationApiType<T> = Api<T> & {
  pagination?: IPagination;
};

export type DefaultApiType<D = any> = {
  data?: D;
  errors?: Record<any, any>;
  status?: string;
  status_code?: number;
};

export type PageableDataResponse<ContentType = any> = DefaultApiType<PaginationApiType<ContentType>>;

export type DefaultApiMessageResponse = DefaultApiType<{ message?: string }>;

export type FilterItem = {
  id?: number | string;
  quantity?: number;
  filter_name?: string;
  index?: number;
  childs?: FilterItem[];
  checked?: boolean;
  translated?: string;
  code?: string;
};

export interface FilterGroup {
  filter_name?: string;
  filter_type?: string;
  filters?: FilterItem[] | string[] | FilterItem;
}

export type FilterGroupRecord = Record<string, FilterGroup>;

export interface Filters {
  filters?: Record<string, FilterGroup>;
}

export interface HttpOptions {
  headers?: HttpHeaders | Record<string, string | string[]>;
  context?: HttpContext;
  observe?: 'body';
  params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>;
  reportProgress?: boolean;
  withCredentials?: boolean;
}

export type FiltersDataResponse = DefaultApiType<Filters>;

export interface SpecialCampaignData {
  activated_at?: string;
  capture_mobile_url?: string;
  capture_url?: string;
  categories?: IDeviceType[];
  configuration_count?: number;
  conversion_rate?: string | null;
  conversion_types?: number[];
  countries?: ICountry[];
  id?: number;
  incent_types?: IDeviceType[];
  lang_name?: string[];
  like_status?: boolean;
  likes?: number;
  live?: boolean;
  logo?: string;
  name?: string;
  payout_time?: number;
  cookies?: number;
  preview_url?: string;
  rate?: string;
  recommended?: boolean;
  short_description?: Record<string, string>[];
  status?: string;
  type?: ICampaignType[];
  beginner?: boolean;
  is_new?: boolean;
  payment?: string;
  commision_value?: string;
  exclusive?: boolean;
  exclusive_user_has_access?: boolean;
  blocked_reason?: string;
  additional_requirements?: boolean;
  rating?: number;
}

export interface SpecialCampaignsResponse {
  campaigns?: SpecialCampaignData[];
}

export type FilterType = Record<string, string | number | null | (string | number | null)[]>;

export interface ISocketNotification {
  id: number;
  link: string;
  message: string;
  title: string;
  type: number;
}

export interface SMSLockerRequestParams {
  order_field: string;
  order_type: string;
  page: number;
  per_page: number;
  search: string;
}

export interface IEnlargeSectionData {
  name: string;
  icon: string;
  section: string;
  negative: string;
}

export interface ISortMethodData {
  order_type: string;
  order_field: string;
}

export interface ISelectedDateRangeData {
  selectedRange: string;
  dateFrom: NgbDate;
  dayFrom: string;
  dateTo: NgbDate;
  dayTo: string;
}

/**
 * @TODO fix typo (dateRange instead of dataRange) in whole application where this type is used (where date-filter component is used)
 */
export interface IEmittedDateRange {
  name: string;
  dataRange: ISelectedDateRangeData;
}

export interface IConfigurationTranslation {
  help?: null | string;
  label?: string;
  lang?: string;
}

export interface ICampaignConfigFieldData {
  id?: number;
  system_name?: string;
  config_values?: string;
  program_field_id?: number;
  translations?: IConfigurationTranslation[];
  type?: string;
  values?: string | null | Record<string, string | number>[] | string[];
  required?: boolean;
  value?: any;
}

export interface IPostbackConfigFieldData {
  system_name?: string;
  value?: number | string;
  values?: number[] | Record<number, string>;
}

export interface ICheckboxValue {
  value: string[];
  system: string;
}

export interface IRadioCheckboxValue {
  title: string;
  value: string | number;
}

export interface ICommonInputValue {
  value: string;
  system: string;
}

export interface IPhoneNumberValue {
  value: {
    phone: string;
  };
  name: string;
}

export interface ICommonInputValidationState {
  name: string;
  state: boolean;
}

export type CampaignConfigurationModalTypes =
  | 'create_configuration'
  | 'edit_configuration'
  | 'create_direct_link'
  | 'edit_link_configuration'
  | 'assign_number'
  | 'edit_number'
  | 'add_promotion'
  | 'choose_button'
  | 'create_ppi_configuration'
  | 'edit_ppi_configuration';

export interface ISimpleResponseData {
  message?: string;
}

export interface ITopMenuSetterData {
  loading: boolean;
  numberOfAvailableConfigurations: number;
  arrayID: number;
  urlConfigurationDefined: boolean;
}

export interface IActionButtonData {
  text: string;
  icon: string;
  class: string;
  external: boolean;
}

export interface ICampaignSelectionSwitch {
  name: string;
  id: string;
}

export interface IToggleSwitchStateData {
  state: boolean;
  setting: string;
}

export interface IConfigTrackingDomain {
  id: number;
}

export interface ICreatedConfigurationConfigData {
  config: ProgramConfiguration;
  trackingDomain: IConfigTrackingDomain;
}

export interface ICampaignsPageLayout {
  name: string;
  icon: string;
}

export interface ICheckedFilterSubelements {
  values: number[];
  elementID: number | string;
  parentElement?: FilterItem;
}

export interface IConfigurationPayment {
  step?: number;
  system_name?: string | null;
  name?: string | null;
  prefix?: string | null;
}

export interface IStepPayment {
  system_name: string;
  name: string;
  prefixes?: string[];
}

export interface IPremiumSMSCampaignPaymentConfig {
  amount_of_steps: number;
  default_configuration: boolean;
  configuration_payments: IConfigurationPayment[];
  step_payments: IStepPayment[];
}

export interface ILockerRouteData {
  id?: string | null;
  title?: string | null;
}

export interface ITrackingDomainParameters {
  tracking_domain_id: string;
  ml_sub1: string;
  ml_sub2: string;
  ml_sub3: string;
  ml_sub4: string;
  ml_sub5: string;
}

export interface ITestABItem {
  name: string;
  id?: number;
  linkConfigurations: ITestABLinkData[];
  additionalFunctions: ITrackingDomainParameters;
  fallbackDesktop?: number;
  fallbackMobile?: number;
}
export interface ITestABLinkItem {
  id: number;
  name: string;
  active: boolean;
  icons?: string[];
  extraData?: ISingleCampaign;
}

export interface ITestABLinkData {
  id: number;
  movement: number;
  device_id?: number;
}

export interface ISmartlinkCountryItem {
  code: string;
  id: number;
  name: string;
  active: boolean;
}

export interface ISmartlinkDataItem {
  name: string;
  id?: number;
  countryConfigurations: ISmartlinkCountryData[];
  additionalFunctions: ITrackingDomainParameters;
  fallbackDesktop?: number;
  fallbackMobile?: number;
}

export interface ISmartlinkCountryData {
  countrySymbol: string;
  countryId: number;
  configurationForAllDevices: boolean;
  allDevices?: number;
  desktop?: number;
  mobileAndroid?: number;
  mobileIOS?: number;
}

export interface ISmartlinkAndTestABLinkItem {
  id: number;
  name?: string;
  movement: number;
  device_id: number;
}

export interface ISmartlinkAndTestABCountryData {
  countrySymbol: string;
  countryId: number;
  configurationForAllDevices: boolean;
  allDevices: ISmartlinkAndTestABLinkItem[];
  desktop: ISmartlinkAndTestABLinkItem[];
  mobileAndroid: ISmartlinkAndTestABLinkItem[];
  mobileIOS: ISmartlinkAndTestABLinkItem[];
}

export interface ISmartlinkItem {
  name: string;
  id: number;
  createdAt: Date;
  type: string;
  accepted?: boolean;
  campaign?: ISingleCampaign | SpecialCampaignData | any;
  configurationData?: ICampaignConfigFieldData[];
  configURL?: string;
  configID?: number;
}

export interface ISmartlinkTestABItem {
  name: string;
  id?: number;
  countryConfigurations: ISmartlinkAndTestABCountryData[];
  additionalFunctions: ITrackingDomainParameters;
  fallbackDesktop?: number;
  fallbackMobile?: number;
}

export interface IGoal {
  id: number;
  name: string;
  rate: string;
}
